import {
  IdeaHistoryDef,
  PnlSummary,
} from '../../../../../apis/idea/idea-api.def.ts'
import Col from 'antd/es/col'
import Row from 'antd/es/row'
import Tabs, { TabsProps } from 'antd/es/tabs'
import IdeyaTypography from '../../../../../common/antd/ideya-typography.tsx'
import { useAppSelector } from '../../../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../../../features/setting/setting.slice.ts'
import { currencyValueFormatter } from '../../../../../utils/formatters.ts'
import { ValueFormatterParams } from 'ag-grid-enterprise'
import { IdeaPnlLineChart } from './idea-pnl-line-chart.tsx'
import { PnlHistoryTable } from './pnl-history-table.tsx'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import Alert from 'antd/es/alert/Alert'

interface IdeaHistoryProps {
  historyData: IdeaHistoryDef
  pnlData: PnlSummary
}

export const IdeaHistory: React.FC<IdeaHistoryProps> = ({
  historyData,
  pnlData,
}) => {
  const themeStyle = useAppSelector(selectThemeStyle)
  const { t } = useTranslation()

  const hasHistoricalData = pnlData.dailyPnls?.some(
    (item) =>
      item.positionDate &&
      pnlData.bookActiveDate &&
      dayjs(item.positionDate).isBefore(dayjs(pnlData.bookActiveDate))
  )

  const HistoricalPnlText = () => {
    const bookActiveDate = pnlData.bookActiveDate

    // Find the first dailyPnls item on or after bookActiveDate
    const lastInactiveRow = pnlData?.dailyPnls
      ?.filter(
        (item) =>
          item.positionDate &&
          dayjs(item.positionDate).isBefore(dayjs(bookActiveDate))
      )
      ?.sort((a, b) => dayjs(b.positionDate).diff(dayjs(a.positionDate)))[0]

    const avgPrice = lastInactiveRow?.eodPrice
    const currency = pnlData?.ticker.currency

    return (
      <Alert
        closable
        showIcon
        message={`Notice: Official start date is ${dayjs(bookActiveDate).format('MMM DD')} - avg cost is ${currencyValueFormatter(
          { value: avgPrice } as ValueFormatterParams,
          { decimal: 2, currency }
        )} (calculated using ${dayjs(lastInactiveRow?.positionDate).format('MMM DD')} EOD price). PnL is calculated from your official date; entries before ${dayjs(
          bookActiveDate
        ).format('MMM DD')} are shown in gray.`}
        type={'info'}
        style={{ margin: '16px 0' }}
      />
    )
  }

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: t('idea_list.idea_modal.idea_history.line_chart_view'),
      children: (
        <IdeaPnlLineChart
          bookActiveDate={pnlData.bookActiveDate}
          data={pnlData.dailyPnls}
          currency={historyData.idea.tickerCurrency ?? ''}
        />
      ),
    },
    {
      key: '2',
      label: t('idea_list.idea_modal.idea_history.table_view'),
      children: (
        <PnlHistoryTable
          bookActiveDate={pnlData.bookActiveDate}
          dailyPnlData={pnlData.dailyPnls}
          currency={historyData.idea.tickerCurrency ?? ''}
        />
      ),
    },
  ]

  return (
    <>
      <div
        style={{
          marginTop: themeStyle.paddingLG,
          marginBottom: themeStyle.marginXL,
        }}
      >
        <Row>
          <Col span={4}>
            <IdeyaTypography.Text type={'secondary'}>
              {t('idea_list.idea_modal.idea_history.daily_pnl')}
            </IdeyaTypography.Text>
          </Col>
          <Col span={6}>
            <IdeyaTypography.Text type={'secondary'}>
              {t('idea_list.idea_modal.idea_history.quarter_pnl')}
            </IdeyaTypography.Text>
          </Col>
          <Col span={8}>
            <IdeyaTypography.Text type={'secondary'}>
              {t('idea_list.idea_modal.idea_history.total_pnl')}
            </IdeyaTypography.Text>
          </Col>
        </Row>
        <Row>
          <Col span={4}>
            <IdeyaTypography.Text>
              {currencyValueFormatter(
                {
                  value: pnlData.dailyPnl,
                  data: historyData.idea,
                } as ValueFormatterParams,
                { decimal: 0, currency: 'USD' }
              )}
            </IdeyaTypography.Text>
          </Col>
          <Col span={6}>
            <IdeyaTypography.Text>
              {currencyValueFormatter(
                {
                  value: pnlData.quarterPnl,
                  data: historyData.idea,
                } as ValueFormatterParams,
                { decimal: 0, currency: 'USD' }
              )}
            </IdeyaTypography.Text>
          </Col>
          <Col span={8}>
            <IdeyaTypography.Text>
              {currencyValueFormatter(
                {
                  value: pnlData.totalPnl,
                  data: historyData.idea,
                } as ValueFormatterParams,
                { decimal: 0, currency: 'USD' }
              )}
            </IdeyaTypography.Text>
          </Col>
        </Row>
      </div>
      <Row style={{ marginBottom: themeStyle.marginXS }}>
        <IdeyaTypography.Title level={4}>
          {t('idea_list.idea_modal.idea_history.historical_daily_pnl')}
        </IdeyaTypography.Title>
      </Row>
      {hasHistoricalData && <HistoricalPnlText />}
      <Tabs defaultActiveKey={'1'} items={items} />
    </>
  )
}
